import { HttpErrorResponse } from '@angular/common/http';

/**
 * Erreur API normalisée. Le backend renvoie `{"detail": "..."}` — mais detail
 * peut aussi être un objet structuré (ex. inquiry_error :
 * `{code, message, errors}`) : le message lisible va dans `detail`, l'objet
 * complet dans `data`.
 */
export class AppError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly url: string,
    readonly data?: Record<string, unknown>,
  ) {
    super(detail);
    this.name = 'AppError';
  }

  /** Code structuré éventuel (ex. 'inquiry_error', 'payment_intent_unexpected_state'). */
  get code(): string | null {
    const code = this.data?.['code'];
    return typeof code === 'string' ? code : null;
  }
}

const GENERIC_BY_STATUS: Record<number, string> = {
  0: 'Impossible de contacter le serveur. Vérifiez votre connexion.',
  400: 'Requête invalide.',
  401: 'Authentification requise.',
  403: 'Accès refusé.',
  404: 'Ressource introuvable.',
  409: 'Opération refusée dans l’état actuel.',
  422: 'Données invalides.',
  500: 'Erreur interne du serveur.',
  503: 'Service en maintenance.',
};

export function toAppError(err: HttpErrorResponse): AppError {
  const raw = err.error?.detail;
  let detail: string | undefined;
  let data: Record<string, unknown> | undefined;

  if (typeof raw === 'string' && raw) {
    detail = raw;
  } else if (raw && typeof raw === 'object') {
    data = raw as Record<string, unknown>;
    const message = data['message'] ?? data['detail'];
    if (typeof message === 'string') detail = message;
  }

  return new AppError(
    err.status,
    detail ?? GENERIC_BY_STATUS[err.status] ?? 'Erreur inattendue.',
    err.url ?? '',
    data,
  );
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError;
}
